import { Button, Input } from "@ondit/ui";
import { type FormEvent, useEffect, useState } from "react";
import { CodeEntry } from "~/common/auth/code-entry";
import { AuthPanel } from "~/common/auth/panel";
import { ApiError, api, providerLoginUrl, safeNext } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { GithubMark, GoogleMark, Logo } from "~/shell/logo";

type Mode = "sign-in" | "sign-up";

/**
 * The way in.
 *
 * Sign-up asks for an address and a password on one screen rather than two.
 * Splitting them is the fashion, and it buys a progress bar at the cost of a
 * second round trip on the one screen where a person is deciding whether this
 * is worth their time.
 *
 * Both provider buttons and the password form are offered at once. Which one a
 * person can use depends on facts we cannot see, and a chooser in front of them
 * asks a question nobody has an opinion about.
 *
 * Which providers exist at all is not one of those facts: a deployment with no
 * Google app registered can only answer that button with `misconfigured`, so
 * the page asks the server first and draws what it can honour.
 */
export function LoginPage() {
	const t = useT();
	const { locale } = useI18n();
	const params = new URLSearchParams(window.location.search);
	// Only a path on this origin: the query string is whatever the link said.
	const next = safeNext(params.get("next"));

	const [mode, setMode] = useState<Mode>(params.get("mode") === "sign-up" ? "sign-up" : "sign-in");
	/** `null` until the server has answered; nothing is drawn on a guess. */
	const [providers, setProviders] = useState<{ google: boolean; github: boolean } | null>(null);
	const [email, setEmail] = useState("");
	const [secret, setSecret] = useState("");
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	const [problem, setProblem] = useState<string | null>(null);
	/** The address is unconfirmed, so the only useful action is to resend. */
	const [unverified, setUnverified] = useState(false);
	// The OAuth callback lands here with a code rather than prose: the refusal
	// has to read in both languages, and the API origin is not a page.
	const failure = params.get("error");
	const oauthProblem =
		failure && ["denied", "state", "code", "provider", "no_email"].includes(failure)
			? t(failure === "denied" ? "login.denied" : `login.error.${failure}`)
			: null;

	useEffect(() => {
		let live = true;
		api
			.authProviders()
			.then((available) => live && setProviders(available))
			// A deployment that cannot answer this is not a deployment that lost
			// its provider apps, and hiding both would leave somebody who only
			// ever signed in with Google no way in at all. Offer them and let the
			// round trip say what it finds.
			.catch(() => live && setProviders({ google: true, github: true }));
		return () => {
			live = false;
		};
	}, []);

	async function submit(event: FormEvent) {
		event.preventDefault();
		// A second Enter while the first request is out would register twice.
		if (busy) return;
		setBusy(true);
		setNotice(null);
		setProblem(null);
		setUnverified(false);
		try {
			if (mode === "sign-up") {
				await api.register(email, secret, locale);
				// The answer is the same whether or not the address is already
				// an account — otherwise this screen tells a stranger who has
				// one. Somebody who already signed up gets a fresh code here;
				// somebody who already confirmed signs in with their password.
				// The code goes into the boxes on this same screen.
				setUnverified(true);
				setNotice(t("login.codeSent"));
			} else {
				await api.signIn(email, secret);
				// A full load rather than a router push: the session cookie is
				// new, and every query the shell makes has to be re-run with it.
				window.location.assign(next);
			}
		} catch (error) {
			if (error instanceof ApiError && error.messageKey === "errors.forbidden.email_unverified") {
				setUnverified(true);
				setNotice(t("login.unverified"));
			} else {
				setProblem(error instanceof ApiError ? error.localized(locale) : String(error));
			}
		} finally {
			setBusy(false);
		}
	}

	async function confirm(code: string) {
		setBusy(true);
		setProblem(null);
		try {
			await api.verifyEmail(email, code);
			window.location.assign(next);
		} catch (error) {
			if (error instanceof ApiError && error.messageKey === "errors.forbidden.code_expired") {
				setProblem(t("login.codeExpired"));
			} else {
				setProblem(error instanceof ApiError ? error.localized(locale) : String(error));
			}
		} finally {
			setBusy(false);
		}
	}

	async function resend() {
		setBusy(true);
		try {
			const { sent } = await api.resendVerification(email);
			// `sent` is the truth about the send, so the notice can be too.
			setNotice(sent ? t("login.resent") : t("login.resendFailed"));
		} catch {
			setNotice(t("login.resendFailed"));
		} finally {
			setBusy(false);
		}
	}

	async function forgot() {
		if (!email) {
			setProblem(t("login.emailFirst"));
			return;
		}
		setBusy(true);
		setProblem(null);
		try {
			await api.forgotPassword(email);
			setNotice(t("login.resetSent"));
		} catch (error) {
			setProblem(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="login">
			<AuthPanel />

			<div className="login-form">
				<a className="brand login-brand" href="/">
					<Logo size="lg" />
				</a>
				<form className="login-card" onSubmit={submit}>
					<h1>{t(mode === "sign-up" ? "login.signUp" : "login.signIn")}</h1>

					{oauthProblem && <div className="banner banner-warn">{oauthProblem}</div>}
					{problem && <div className="banner banner-warn">{problem}</div>}
					{notice && <div className="banner">{notice}</div>}

					<div className="field">
						<label htmlFor="email">{t("login.email")}</label>
						<Input
							id="email"
							type="email"
							autoComplete="email"
							required
							placeholder="you@example.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</div>

					{unverified ? (
						<>
							<CodeEntry disabled={busy} onComplete={confirm} />
							<Button variant="default" type="button" onClick={resend} disabled={busy}>
								{t("login.resend")}
							</Button>
						</>
					) : (
						<>
							<div className="field">
								<label htmlFor="password">{t("login.password")}</label>
								<Input
									id="password"
									type="password"
									// The browser needs to know which of the two this is,
									// or it offers to save the wrong thing.
									autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
									required
									minLength={8}
									value={secret}
									onChange={(e) => setSecret(e.target.value)}
								/>
								{mode === "sign-up" && <span className="hint">{t("login.passwordHint")}</span>}
							</div>

							<Button variant="primary" type="submit" disabled={busy}>
								{busy ? t("login.working") : t(mode === "sign-up" ? "login.createAccount" : "login.continue")}
							</Button>
						</>
					)}

					{mode === "sign-in" && (
						<Button variant="link" type="button" onClick={forgot} disabled={busy}>
							{t("login.forgot")}
						</Button>
					)}

					{(providers?.google || providers?.github) && (
						<div className="login-or">
							<span>{t("login.or")}</span>
						</div>
					)}

					{providers?.google && (
						<Button asChild variant="default">
							<a className="login-provider" href={providerLoginUrl("google", next)}>
								<GoogleMark />
								{t("login.withGoogle")}
							</a>
						</Button>
					)}
					{providers?.github && (
						<Button asChild variant="default">
							<a className="login-provider" href={providerLoginUrl("github", next)}>
								<GithubMark />
								{t("login.withGithub")}
							</a>
						</Button>
					)}

					<p className="login-foot">
						{t(mode === "sign-up" ? "login.haveAccount" : "login.noAccount")}{" "}
						<Button variant="link" type="button" onClick={() => setMode(mode === "sign-up" ? "sign-in" : "sign-up")}>
							{t(mode === "sign-up" ? "login.signIn" : "login.signUp")}
						</Button>
					</p>
				</form>
			</div>
		</div>
	);
}
